import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { CheckCircle2, MailCheck } from "lucide-react";
import Seo from "../components/Seo";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh, user } = useAuth();
  const [token, setToken] = useState(params.get("token") || "");
  const [status, setStatus] = useState(null); // null | 'verifying' | 'ok' | 'err'
  const [error, setError] = useState("");

  const verify = async (t) => {
    setStatus("verifying"); setError("");
    try {
      await api.post("/auth/verify", { token: t });
      setStatus("ok");
      await refresh();
      setTimeout(() => nav("/dashboard"), 1200);
    } catch (e) {
      setStatus("err");
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Invalid token");
    }
  };

  useEffect(() => {
    const t = params.get("token");
    if (t) verify(t);
  }, [params]);

  return (
    <div className="mx-auto max-w-md px-6 lg:px-8 py-20" data-testid="verify-email-page">
      <Seo title="Verify your email — BizzAtlas" description="Confirm your email to start using BizzAtlas." />
      <div className="text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-400 mb-2">/ Verification</div>
      <h1 className="font-display text-4xl tracking-tight">Verify your email.</h1>
      {status === "ok" ? (
        <div className="mt-8 p-6 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800" data-testid="verify-ok">
          <CheckCircle2 className="h-6 w-6 mb-2" />
          <div className="font-display text-2xl">You&apos;re verified.</div>
          <p className="text-sm mt-1">Redirecting to your dashboard…</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-zinc-600 mt-2 mb-6">
            Click the link in the email we sent you, or paste the token below.
          </p>
          <div className="flex items-center gap-2">
            <Input value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste your token" data-testid="verify-token-input" />
            <Button onClick={() => verify(token)} disabled={!token || status === "verifying"} className="btn-brand" data-testid="verify-submit-btn">
              <MailCheck className="h-4 w-4 mr-1" />
              {status === "verifying" ? "Verifying…" : "Verify"}
            </Button>
          </div>
          {status === "err" && <div className="mt-4 text-sm text-red-600" data-testid="verify-error">{error}</div>}
          {user && !user.email_verified && (
            <div className="mt-6 text-xs text-zinc-500">
              Need a new email? Use the &ldquo;Resend email&rdquo; link in the orange banner at the top of the app.
            </div>
          )}
        </>
      )}
    </div>
  );
}
